import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProjectCard } from './ProjectCard';
import { ProjectLightbox } from './ProjectLightbox';
import { PROJECTS, PROJECT_TABS } from '../../../data/projects';
import '../../../styles/projects.css';

const gridVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  exit:    { opacity: 0, transition: { duration: 0.15 } },
};

const cardVariants = {
  hidden:  { opacity: 0, y: 24, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit:    { opacity: 0, y: -10, scale: 0.97, transition: { duration: 0.2 } },
};

export function Projects() {
  const [activeTab, setActiveTab] = useState('web');
  const [lightboxData, setLightboxData] = useState({ isOpen: false, images: [], initialIndex: 0 });

  const filtered = PROJECTS.filter(p => p.category === activeTab);

  const openLightbox = (images, initialIndex) => {
    setLightboxData({ isOpen: true, images, initialIndex });
  };

  const closeLightbox = () => {
    setLightboxData({ ...lightboxData, isOpen: false });
  };

  return (
    <section className="projects" id="projects" aria-label="Projects">
      <div className="projects-container">

        {/* Section header */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55 }}
        >
          <span className="section-eyebrow">What I've built</span>
          <h2 className="section-title">Projects</h2>
          <span className="accent-line" aria-hidden="true" />
          <p style={{ color: 'var(--color-text-muted)', maxWidth: '520px', marginTop: 'var(--space-2)' }}>
            A selection of projects I've developed during my journey as a student and freelancer.
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="projects-tabs"
          role="tablist"
          aria-label="Project categories"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          {PROJECT_TABS.map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              aria-controls={`panel-${key}`}
              className={`tab-btn${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </motion.div>

        {/* Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-label={`${activeTab} projects`}
            className="projects-grid"
            variants={gridVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {filtered.length > 0 ? (
              filtered.map(project => (
                <motion.div key={project.id} variants={cardVariants}>
                  <ProjectCard
                    title={project.title}
                    subtitle={project.subtitle}
                    description={project.description}
                    tech={project.tech}
                    images={project.images}
                    onClick={openLightbox}
                  />
                </motion.div>
              ))
            ) : (
              <motion.p
                variants={cardVariants}
                style={{ color: 'var(--color-text-subtle)', padding: 'var(--space-8) 0' }}
              >
                No projects in this category yet.
              </motion.p>
            )}
          </motion.div>
        </AnimatePresence>

      </div>

      <AnimatePresence>
        {lightboxData.isOpen && (
          <ProjectLightbox 
            images={lightboxData.images} 
            initialIndex={lightboxData.initialIndex} 
            onClose={closeLightbox} 
          />
        )}
      </AnimatePresence>
    </section>
  );
}
